// src/utils/dentallyLeadSyncApi.js
//
// Transport for the per-lead "how does the CRM differ from the linked Dentally
// patient?" panel. Practice/lead scoped, read-then-apply, and deliberately thin:
// every function returns the raw callApi result ({ ok, status, data, error }) so
// `useDentallyLeadSync` owns all loading / error / retry presentation.
//
// Contract (backend agent `be-lead-sync`):
//   GET  .../leads/{leadId}/dentally/sync/
//        -> { linked, dentally_patient_id, checked_at,
//             differences: [{ field, label, crm_value, dentally_value }],
//             can_apply, provider_error: null | { detail, code } }
//   POST .../leads/{leadId}/dentally/sync/  { idempotency_key, fields: [...] }
//        -> { applied: [...], skipped: [{ field, reason }], differences: [remaining] }
//        -> 409 { code: "lead_archived" } when the lead is archived.
//
// `suppressServerErrorDispatch` keeps a Dentally outage out of the lead page's
// global error snackbar — this panel is supplementary and must never make the
// lead detail look broken.
import { callApi } from './apiCall';
import newIdempotencyKey from './idempotencyKey';

export { newIdempotencyKey };

const QUIET = { suppressServerErrorDispatch: true };

export const leadSyncUrl = (practiceId, leadId) => `/api/v1/practices/${practiceId}/leads/${leadId}/dentally/sync/`;

export function fetchDentallyLeadSync(practiceId, leadId, options = {}) {
  return callApi(leadSyncUrl(practiceId, leadId), 'GET', null, false, { ...QUIET, ...options });
}

export function applyDentallyLeadSync(practiceId, leadId, { idempotencyKey, fields }) {
  return callApi(
    leadSyncUrl(practiceId, leadId),
    'POST',
    { idempotency_key: idempotencyKey, fields },
    false,
    QUIET,
  );
}

// --- CRM -> Dentally -------------------------------------------------------
//
// The opposite direction, added once the client gained a patient-update call.
// A separate endpoint rather than another verb on the sync URL, because the
// two directions have different permissions and different failure meanings.
//
//   POST .../leads/{leadId}/dentally/push/  { confirm_identity?: bool }
//     -> 200 { outcome, fields: [...], error_code, push: {...} }
//        outcome: synced | already_synced | nothing_to_send | conflict
//                 | not_linked | no_integration | skipped_correction
//     -> 202 for a retryable failure: the row stays PENDING and the server's
//        sweeper will try again, so it is information, not a dead end.
//     -> 403 / 409 { outcome: "blocked", blocked_reason } — the same
//        vocabulary the pull direction uses for a blocked apply.
//
// `confirm_identity` is the operator agreeing to change the NAME or DATE OF
// BIRTH on the patient record. Without it the server holds those back, because
// they are the fields Dentally matches patients on.
export const leadPushUrl = (practiceId, leadId) => `/api/v1/practices/${practiceId}/leads/${leadId}/dentally/push/`;

export function pushDentallyLeadDetails(practiceId, leadId, { confirmIdentity = false } = {}) {
  return callApi(
    leadPushUrl(practiceId, leadId),
    'POST',
    { confirm_identity: confirmIdentity },
    false,
    QUIET,
  );
}
